// Strategy Card Component - Displays individual strategy stats

import { StrategyState } from '../lib/types';
import { getStrategySummary } from '../lib/strategies';
import { useTradingStore } from '../lib/store';

interface StrategyCardProps {
  strategy: 'A' | 'B';
  state: StrategyState;
  enabled: boolean;
  qualifyingCount: number;
}

export function StrategyCard({ strategy, state, enabled, qualifyingCount }: StrategyCardProps) {
  const toggleStrategy = useTradingStore((state) => state.toggleStrategy);
  const resetStrategy = useTradingStore((state) => state.resetStrategy);
  const setBankroll = useTradingStore((state) => state.setBankroll);
  const summary = getStrategySummary(state);
  
  const isProfit = state.totalPnL >= 0;
  const returnPercent = state.initialBankroll > 0 
    ? ((state.bankroll - state.initialBankroll) / state.initialBankroll * 100)
    : 0;

  return (
    <div className={`strategy-card ${enabled ? 'enabled' : 'disabled'}`}>
      <div className="strategy-header">
        <div className="strategy-title">
          <span className="strategy-badge">{strategy}</span>
          <h3>{state.name}</h3>
        </div>
        <label className="toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={() => toggleStrategy(strategy)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>
      
      <p className="strategy-description">{state.description}</p>
      
      <div className="strategy-stats">
        <div className="stat-row main-stat">
          <span className="stat-label">Bankroll</span>
          <span className={`stat-value ${isProfit ? 'profit' : 'loss'}`}>
            ${state.bankroll.toFixed(2)}
          </span>
        </div>
        
        <div className="stat-row">
          <span className="stat-label">Return</span>
          <span className={`stat-value ${isProfit ? 'profit' : 'loss'}`}>
            {returnPercent >= 0 ? '+' : ''}{returnPercent.toFixed(1)}%
          </span>
        </div>
        
        <div className="stat-row">
          <span className="stat-label">P&L</span>
          <span className={`stat-value ${isProfit ? 'profit' : 'loss'}`}>
            {state.totalPnL >= 0 ? '+' : ''}${state.totalPnL.toFixed(2)}
          </span>
        </div>
        
        <div className="stat-divider"></div>
        
        <div className="stat-row">
          <span className="stat-label">Win Rate</span>
          <span className="stat-value">{state.winRate.toFixed(1)}%</span>
        </div>
        
        <div className="stat-row">
          <span className="stat-label">Trades</span>
          <span className="stat-value">
            {state.wins}W / {state.losses}L ({state.totalTrades} total)
          </span>
        </div>
        
        <div className="stat-row">
          <span className="stat-label">Pending</span>
          <span className="stat-value">
            {state.trades.filter(t => t.result === 'pending').length}
          </span>
        </div>
        
        <div className="stat-divider"></div>
        
        <div className="stat-row">
          <span className="stat-label">Avg Win</span>
          <span className="stat-value profit">${summary.avgWin.toFixed(3)}</span>
        </div>
        
        <div className="stat-row">
          <span className="stat-label">Avg Loss</span>
          <span className="stat-value loss">-${summary.avgLoss.toFixed(3)}</span>
        </div>
        
        <div className="stat-row">
          <span className="stat-label">Max Drawdown</span>
          <span className="stat-value">{summary.maxDrawdown.toFixed(1)}%</span>
        </div>
        
        <div className="stat-divider"></div>
        
        <div className="stat-row highlight">
          <span className="stat-label">Qualifying Markets</span>
          <span className="stat-value">{qualifyingCount}</span>
        </div>
      </div>
      
      <div className="strategy-actions">
        <div className="bankroll-input">
          <label>Set Bankroll: $</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={state.bankroll}
            onChange={(e) => setBankroll(strategy, parseFloat(e.target.value) || 0)}
          />
        </div>
        <button 
          className="btn-reset"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            resetStrategy(strategy);
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
}
